import { type SetStateAction, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { Button, Popup } from "semantic-ui-react";
import { styled } from "styled-components";

import { RichText } from "simplydo/react/richtext";

import type { CommentState } from "./Discussion";

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 0.5px solid #c9c9c9;
  border-radius: 5px;
  background-color: #fff;
  padding: 6px;
`;

export const MediaContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1px;
  align-items: flex-start;
  justify-content: flex-start;

  margin: 5px 0;
`;

const ActionArea = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 5px 0 5px;
  border-top: 0.5px solid silver;
  padding-top: 7px;
  padding-bottom: 2px;

  > div {
    display: flex;
    align-items: center;
  }
  > .ui.button {
    margin: 0;
  }
`;

type CommentType = CommentState["string"];
type CommentInputProps = {
  forType: string;
  forId: string;
  currentReply: CommentType;
  updateReply: (
    commentOrUpdater: CommentType | SetStateAction<CommentType>,
  ) => void;
  postComment: () => void;
  discussionId?: string;
  isEditing?: boolean;
  onCancel?: () => void;
};

export const CommentInput = ({
  forType,
  forId,
  currentReply,
  updateReply,
  postComment,
  isEditing,
  onCancel,
}: CommentInputProps) => {
  const { t } = useTranslation();

  return (
    <InputContainer>
      <RichText
        forType={forType}
        forId={forId}
        preset="full"
        placeholder="Write a comment..."
        value={currentReply.messageHtml}
        emptyReturnValue={""}
        autoHideMenu={false}
        autoFocus={true}
        disableBorder
        onChange={(html, textValue) =>
          updateReply((prev) => ({
            ...prev,
            messageHtml: html,
            messageText: textValue,
          }))
        }
      />

      <ActionArea>
        <div></div>
        <div>
          {onCancel ?
            <Button basic onClick={onCancel} content={t("generic.cancel")} />
          : null}
          <Button
            basic
            icon={isEditing ? undefined : "send"}
            content={isEditing && t("generic.save")}
            onClick={() => postComment()}
            loading={currentReply.loading || false}
            disabled={!currentReply.messageText}
          />
        </div>
      </ActionArea>
    </InputContainer>
  );
};
